/**
 * Global Operational Context.
 *
 * One shared state object for the dimensions an operator carries
 * across major workspaces (Today, Executive, Alerts, Requests,
 * Scorecard, Goal Plan, Goals & Performance, People & Talent):
 *
 *   - location  (e.g. 'PHX' | 'ALL')
 *   - periodType ('weekly' | 'monthly' | 'quarterly' | 'yearly')
 *   - year      (e.g. '2026')
 *   - month     ('1'…'12')        — only meaningful when periodType=monthly
 *   - quarter   ('1'…'4')         — only meaningful when periodType=quarterly
 *   - week      ('1'…'53')        — only meaningful when periodType=weekly
 *   - kpi       (optional KPI key the operator wants to focus on)
 *
 * Persistence rules — three-tier priority (mirrors urlState.js):
 *   1. URL params         (loc, pt, yr, mo, qt, wk, kpi)
 *   2. localStorage       (key: OPERATIONAL_CONTEXT_STORAGE_KEY)
 *   3. defaults           (no location, monthly, current year/month)
 *
 * Changing any dimension:
 *   - writes the new value back to the URL via replaceState (no
 *     history clutter — filter tweaks don't pollute the back button)
 *   - mirrors to localStorage so the *next* session reloads the same
 *     working context
 *
 * Navigation between workspaces preserves context because the URL
 * params themselves persist; App.js's handleNavigate already merges
 * sticky keys into outbound links, and the provider re-hydrates from
 * the URL on every mount.
 *
 * This module is intentionally a thin overlay — it does NOT replace
 * App.js's existing selectedLocationCode / selectedPeriod sync. It
 * coexists so we can introduce the OperationalContextBar surface by
 * surface without breaking any of the 90+ existing test suites.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  buildSearchString,
  readUrlState,
  URL_KEY_KPI,
  URL_KEY_LOCATION,
  URL_KEY_MONTH,
  URL_KEY_PERIOD_TYPE,
  URL_KEY_QUARTER,
  URL_KEY_WEEK,
  URL_KEY_YEAR,
  VALID_PERIOD_TYPES
} from '../utils/urlState';

export const OPERATIONAL_CONTEXT_STORAGE_KEY = 'opsContext.v1';
export const ALL_LOCATIONS_SENTINEL = 'ALL';

const DEFAULT_PERIOD_TYPE = 'monthly';

/**
 * Read the operator's last-saved context from localStorage. Safe to
 * call in SSR / test environments — returns {} on any error.
 */
export function readStoredContext() {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(OPERATIONAL_CONTEXT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

export function writeStoredContext(next) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(OPERATIONAL_CONTEXT_STORAGE_KEY, JSON.stringify(next || {}));
  } catch {
    /* swallow quota / privacy errors — URL is still authoritative */
  }
}

function defaultsForNow() {
  const now = new Date();
  return {
    location: '',
    periodType: DEFAULT_PERIOD_TYPE,
    year: String(now.getUTCFullYear()),
    month: String(now.getUTCMonth() + 1),
    quarter: String(Math.floor(now.getUTCMonth() / 3) + 1),
    week: '',
    kpi: ''
  };
}

/**
 * Resolve the initial operational context using the three-tier
 * priority: URL → localStorage → defaults. Exported so callers /
 * tests can compute the same starting state without mounting the
 * provider.
 *
 * Pass an optional `now` for deterministic tests.
 */
export function resolveInitialContext({ search, stored, now } = {}) {
  const baseDefaults = now ? {
    ...defaultsForNow(),
    year: String(now.getUTCFullYear()),
    month: String(now.getUTCMonth() + 1),
    quarter: String(Math.floor(now.getUTCMonth() / 3) + 1)
  } : defaultsForNow();

  const fromStorage = stored || readStoredContext();
  const fromUrl = readUrlState(search);

  // Merge precedence: defaults < storage < URL.
  const merged = {
    location: fromUrl[URL_KEY_LOCATION] || fromStorage.location || baseDefaults.location,
    periodType: fromUrl[URL_KEY_PERIOD_TYPE] || fromStorage.periodType || baseDefaults.periodType,
    year: fromUrl[URL_KEY_YEAR] || fromStorage.year || baseDefaults.year,
    month: fromUrl[URL_KEY_MONTH] || fromStorage.month || baseDefaults.month,
    quarter: fromUrl[URL_KEY_QUARTER] || fromStorage.quarter || baseDefaults.quarter,
    week: fromUrl[URL_KEY_WEEK] || fromStorage.week || baseDefaults.week,
    kpi: fromUrl[URL_KEY_KPI] || fromStorage.kpi || baseDefaults.kpi
  };

  // Final guard: if the merged periodType is somehow invalid (older
  // localStorage value, manual URL tinkering), fall back to default.
  if (!VALID_PERIOD_TYPES.includes(merged.periodType)) {
    merged.periodType = baseDefaults.periodType;
  }

  return merged;
}

/**
 * Build the URL state slice (the loc/pt/yr/mo/qt/wk/kpi params) for
 * the current context. Empty / default values are dropped so the URL
 * stays clean.
 */
export function contextToUrlState(ctx) {
  // Always emit every key — pass null for the values to DROP so the
  // buildSearchString merge cleans up stale params (e.g. switching
  // from monthly→quarterly must remove the now-meaningless `mo`).
  return {
    [URL_KEY_LOCATION]: (ctx.location && ctx.location !== ALL_LOCATIONS_SENTINEL) ? ctx.location : null,
    [URL_KEY_PERIOD_TYPE]: (ctx.periodType && ctx.periodType !== DEFAULT_PERIOD_TYPE) ? ctx.periodType : null,
    [URL_KEY_YEAR]: ctx.year || null,
    [URL_KEY_MONTH]: (ctx.month && ctx.periodType === 'monthly') ? ctx.month : null,
    [URL_KEY_QUARTER]: (ctx.quarter && ctx.periodType === 'quarterly') ? ctx.quarter : null,
    [URL_KEY_WEEK]: (ctx.week && ctx.periodType === 'weekly') ? ctx.week : null,
    [URL_KEY_KPI]: ctx.kpi || null
  };
}

const OperationalContext = createContext(null);

export function OperationalContextProvider({ children, initial = null, persistToUrl = true }) {
  const [context, setContext] = useState(() => initial || resolveInitialContext());

  // Mirror changes to localStorage so the next session reloads the
  // same working context — purely additive; URL still wins on read.
  useEffect(() => {
    writeStoredContext(context);
  }, [context]);

  // Mirror changes to the URL via replaceState so deep links + refresh
  // preserve context, without polluting the back-button history.
  useEffect(() => {
    if (!persistToUrl) return;
    if (typeof window === 'undefined' || !window.history) return;
    const nextQuery = buildSearchString(contextToUrlState(context), window.location.search);
    const currentQuery = (window.location.search || '').replace(/^\?/, '');
    if (nextQuery === currentQuery) return;
    const nextUrl = nextQuery
      ? `${window.location.pathname}?${nextQuery}${window.location.hash || ''}`
      : `${window.location.pathname}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [context, persistToUrl]);

  // Browser back/forward: re-hydrate context from the URL so any
  // dimension that changed during the navigated-away workspace gets
  // restored on return.
  useEffect(() => {
    if (!persistToUrl || typeof window === 'undefined') return undefined;
    const handler = () => {
      setContext(prev => resolveInitialContext({ stored: prev }));
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [persistToUrl]);

  const updateContext = useCallback((patch) => {
    setContext(prev => {
      const next = { ...prev, ...patch };
      // Switching periodType clears the irrelevant period-anchor
      // fields so a stale month=12 doesn't bleed into a quarterly view.
      if (patch.periodType && patch.periodType !== prev.periodType) {
        if (patch.periodType !== 'monthly') next.month = '';
        if (patch.periodType !== 'quarterly') next.quarter = '';
        if (patch.periodType !== 'weekly') next.week = '';
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    context,
    setContext: updateContext,
    setLocation: (location) => updateContext({ location }),
    setPeriodType: (periodType) => updateContext({ periodType }),
    setYear: (year) => updateContext({ year }),
    setMonth: (month) => updateContext({ month }),
    setQuarter: (quarter) => updateContext({ quarter }),
    setWeek: (week) => updateContext({ week }),
    setKpi: (kpi) => updateContext({ kpi }),
    resetContext: () => setContext(resolveInitialContext())
  }), [context, updateContext]);

  return (
    <OperationalContext.Provider value={value}>{children}</OperationalContext.Provider>
  );
}

/**
 * Read the operational context. Returns a fallback no-op object when
 * called outside the provider so existing pages that haven't yet
 * adopted the context don't blow up.
 */
export function useOperationalContext() {
  const value = useContext(OperationalContext);
  if (value) return value;
  // Inert fallback: lets a component render with the bar disabled when
  // no provider exists in the tree (e.g. legacy tests).
  return {
    context: resolveInitialContext(),
    setContext: () => {},
    setLocation: () => {},
    setPeriodType: () => {},
    setYear: () => {},
    setMonth: () => {},
    setQuarter: () => {},
    setWeek: () => {},
    setKpi: () => {},
    resetContext: () => {}
  };
}

export { OperationalContext };
